/**
 * i18n bootstrap for the provider-facing app.
 *
 * Initializes i18next + react-i18next with the four supported locales and the
 * domain-grouped namespaces. Resource files are imported statically so they are
 * bundled with the app (no network fetch) — important for the installed PWA /
 * offline use. English is the fallback for any missing key.
 *
 * Import this module once, before React renders (see `app/main.tsx`).
 */

import { initReactI18next } from 'react-i18next';
import i18n from 'i18next';
import { Settings as LuxonSettings } from 'luxon';
// English (authoritative) resources
import enAppointments from '@/locales/en/appointments.json';
import enClients from '@/locales/en/clients.json';
import enCommon from '@/locales/en/common.json';
import enErrors from '@/locales/en/errors.json';
import enNavigation from '@/locales/en/navigation.json';
import enOnboarding from '@/locales/en/onboarding.json';
import enPayments from '@/locales/en/payments.json';
import enRevenue from '@/locales/en/revenue.json';
import enSchedule from '@/locales/en/schedule.json';
import enServices from '@/locales/en/services.json';
import enSettings from '@/locales/en/settings.json';
import enStaff from '@/locales/en/staff.json';
// Spanish (US)
import esAppointments from '@/locales/es-US/appointments.json';
import esClients from '@/locales/es-US/clients.json';
import esCommon from '@/locales/es-US/common.json';
import esErrors from '@/locales/es-US/errors.json';
import esNavigation from '@/locales/es-US/navigation.json';
import esOnboarding from '@/locales/es-US/onboarding.json';
import esPayments from '@/locales/es-US/payments.json';
import esRevenue from '@/locales/es-US/revenue.json';
import esSchedule from '@/locales/es-US/schedule.json';
import esServices from '@/locales/es-US/services.json';
import esSettings from '@/locales/es-US/settings.json';
import esStaff from '@/locales/es-US/staff.json';
// French
import frAppointments from '@/locales/fr/appointments.json';
import frClients from '@/locales/fr/clients.json';
import frCommon from '@/locales/fr/common.json';
import frErrors from '@/locales/fr/errors.json';
import frNavigation from '@/locales/fr/navigation.json';
import frOnboarding from '@/locales/fr/onboarding.json';
import frPayments from '@/locales/fr/payments.json';
import frRevenue from '@/locales/fr/revenue.json';
import frSchedule from '@/locales/fr/schedule.json';
import frServices from '@/locales/fr/services.json';
import frSettings from '@/locales/fr/settings.json';
import frStaff from '@/locales/fr/staff.json';
// Portuguese (Brazil)
import ptAppointments from '@/locales/pt-BR/appointments.json';
import ptClients from '@/locales/pt-BR/clients.json';
import ptCommon from '@/locales/pt-BR/common.json';
import ptErrors from '@/locales/pt-BR/errors.json';
import ptNavigation from '@/locales/pt-BR/navigation.json';
import ptOnboarding from '@/locales/pt-BR/onboarding.json';
import ptPayments from '@/locales/pt-BR/payments.json';
import ptRevenue from '@/locales/pt-BR/revenue.json';
import ptSchedule from '@/locales/pt-BR/schedule.json';
import ptServices from '@/locales/pt-BR/services.json';
import ptSettings from '@/locales/pt-BR/settings.json';
import ptStaff from '@/locales/pt-BR/staff.json';
import { setActiveAppLocale, setActiveIntlLocale } from '@/shared/utils/intl-locale';
import {
  FALLBACK_LOCALE,
  SUPPORTED_LOCALE_CODES,
  getIntlLocale,
  normalizeLocale,
  resolveInitialLanguage,
  storeLanguage,
} from './locales';

export const NAMESPACES = [
  'common',
  'navigation',
  'onboarding',
  'services',
  'schedule',
  'staff',
  'appointments',
  'payments',
  'settings',
  'clients',
  'revenue',
  'errors',
] as const;

export const DEFAULT_NS = 'common';

const resources = {
  en: {
    common: enCommon,
    navigation: enNavigation,
    onboarding: enOnboarding,
    services: enServices,
    schedule: enSchedule,
    staff: enStaff,
    appointments: enAppointments,
    payments: enPayments,
    settings: enSettings,
    clients: enClients,
    revenue: enRevenue,
    errors: enErrors,
  },
  'es-US': {
    common: esCommon,
    navigation: esNavigation,
    onboarding: esOnboarding,
    services: esServices,
    schedule: esSchedule,
    staff: esStaff,
    appointments: esAppointments,
    payments: esPayments,
    settings: esSettings,
    clients: esClients,
    revenue: esRevenue,
    errors: esErrors,
  },
  'pt-BR': {
    common: ptCommon,
    navigation: ptNavigation,
    onboarding: ptOnboarding,
    services: ptServices,
    schedule: ptSchedule,
    staff: ptStaff,
    appointments: ptAppointments,
    payments: ptPayments,
    settings: ptSettings,
    clients: ptClients,
    revenue: ptRevenue,
    errors: ptErrors,
  },
  fr: {
    common: frCommon,
    navigation: frNavigation,
    onboarding: frOnboarding,
    services: frServices,
    schedule: frSchedule,
    staff: frStaff,
    appointments: frAppointments,
    payments: frPayments,
    settings: frSettings,
    clients: frClients,
    revenue: frRevenue,
    errors: frErrors,
  },
} as const;

const initialLanguage = resolveInitialLanguage();

/** Keep Luxon + shared Intl formatters in sync with the active app language. */
const syncIntlLocale = (language: string): void => {
  const intlLocale = getIntlLocale(language);
  LuxonSettings.defaultLocale = intlLocale;
  setActiveIntlLocale(intlLocale);
  // Also expose the raw app locale (e.g. `es-US`) for the axios layer to stamp
  // onto API requests as `X-Donvio-Locale`.
  setActiveAppLocale(language);
};

syncIntlLocale(initialLanguage);

void i18n
  .use(initReactI18next)
  .init({
    resources,
    lng: initialLanguage,
    fallbackLng: FALLBACK_LOCALE,
    supportedLngs: SUPPORTED_LOCALE_CODES as string[],
    ns: NAMESPACES as unknown as string[],
    defaultNS: DEFAULT_NS,
    interpolation: {
      // React already escapes output, so i18next interpolation must not.
      escapeValue: false,
    },
    returnNull: false,
    react: {
      useSuspense: false,
    },
  });

i18n.on('languageChanged', language => {
  syncIntlLocale(language);
  storeLanguage(language);
});

/**
 * Apply a server-persisted `preferredLocale` (from the authenticated profile).
 *
 * Unlike `setLanguage`, this does NOT sync back to the server — it only reflects
 * the already-stored server preference locally. Unsupported/empty values are
 * ignored. Returns the locale that was applied, or null if nothing changed.
 */
export const applyServerLocale = (raw?: string | null): string | null => {
  const normalized = normalizeLocale(raw);
  if (!normalized || normalized === i18n.language) return null;
  void i18n.changeLanguage(normalized);
  return normalized;
};

export { useLanguage } from './useLanguage';
export type { UseLanguageResult } from './useLanguage';
export {
  SUPPORTED_LOCALES,
  SUPPORTED_LOCALE_CODES,
  FALLBACK_LOCALE,
  getIntlLocale,
  normalizeLocale,
} from './locales';
export type { SupportedLocale } from './locales';

export default i18n;
